import { useMemo } from 'react'
import { StyleSheet, useWindowDimensions } from 'react-native'
import Animated, {
  Keyframe,
  LinearTransition,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated'

import { Box, Text, useTheme } from '../theme/restyle'
import { type AmountGlyphKind, amountGlyphs } from '../utils/amountGlyphs'
import { MOTION_DURATION, MOTION_EASING, nativeMotion } from '../utils/motion'

// Figma's amount type; `font/*` has no step this large. The placeholder's 20%
// is the design's own value, not `opacity/disabled` (40).
const AMOUNT_FONT_SIZE = 96
const PLACEHOLDER_OPACITY = 0.2
// Ratios, so a fitted amount scales its line box and tracking too. The box must
// stay above the font's natural ~1.16em — iOS honours a shorter one by
// compressing the line and clipping the glyphs.
const LINE_HEIGHT_RATIO = 1.25
const LETTER_SPACING_RATIO = -1.44 / 96
// The design trims to the cap box (68 at 96), which RN can't do — it lays out
// the line box. A negative margin takes that dead space back instead, so the
// glyphs overhang empty gaps rather than being clipped by their parent.
const CAP_BOX_RATIO = 68 / 96
// One character's advance, as a share of its size. The fit is computed from it
// because `adjustsFontSizeToFit` also reads the frame's *height*, collapsing
// the number whenever its row is shorter than the line box.
const CHARACTER_ADVANCE_RATIO = 0.6
const HORIZONTAL_INSET = 16
// Below this the amount is unreadable anyway, so a long entry runs to the edges.
const MIN_FONT_SIZE = 40

const AnimatedBox = Animated.createAnimatedComponent(Box)
const AnimatedText = Animated.createAnimatedComponent(Text)

// A character arriving or leaving re-centres the number, so the ones staying
// slide to their new place rather than cutting to it.
const GLYPH_LAYOUT =
  LinearTransition.duration(MOTION_DURATION).easing(MOTION_EASING)

export interface AmountDisplayProps {
  /** The typed amount, or `''` for the placeholder. */
  value: string
  /** Written before the number. */
  prefix?: string
  /** Tints the amount, matching the message that replaces the balance line. */
  hasError?: boolean
  accessibilityLabel: string
}

/**
 * The withdrawal amount, read-only and driven by the numpad — it looks like a
 * field but isn't one, so tapping it must not open a keyboard.
 *
 * Each character is its own node so it can move on its own: digits rise into the
 * number and drop back out, while the placeholder lifts off the top instead.
 */
export function AmountDisplay({
  value,
  prefix = '$',
  hasError = false,
  accessibilityLabel,
}: AmountDisplayProps) {
  const theme = useTheme()
  const { width } = useWindowDimensions()
  const isEmpty = value === ''
  const glyphs = amountGlyphs(value, prefix)
  const text = glyphs.map((glyph) => glyph.char).join('')
  const fontSize = fitAmountFontSize(text, width - HORIZONTAL_INSET * 2)
  const overhang = (fontSize * (LINE_HEIGHT_RATIO - CAP_BOX_RATIO)) / 2

  const lineHeight = fontSize * LINE_HEIGHT_RATIO
  // A whole line box of travel, so a character is clear of the row before it is
  // dropped.
  const slides = useMemo(
    () => ({
      below: slideKeyframes(lineHeight),
      above: slideKeyframes(-lineHeight),
    }),
    [lineHeight],
  )

  // The prefix outlives both the placeholder and the digits, so it can only be
  // dimmed by fading with them, not by mounting.
  const prefixDimming = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one (`useAnimatedStyle2`) in the bundle.
    'worklet'
    return {
      opacity: withTiming(isEmpty ? PLACEHOLDER_OPACITY : 1, {
        duration: MOTION_DURATION,
        easing: MOTION_EASING,
      }),
    }
  })

  return (
    // The window the characters slide through. What it clips is the dead space
    // the margin takes back, never the glyphs.
    <Box
      accessible
      accessibilityLabel={accessibilityLabel}
      accessibilityValue={{ text }}
      style={[styles.row, { marginVertical: -overhang }]}
    >
      {glyphs.map(({ key, char, kind }) => (
        <AnimatedBox
          key={key}
          entering={nativeMotion(slides[glyphSide(kind, isEmpty)].entering)}
          exiting={nativeMotion(slides[glyphSide(kind, isEmpty)].exiting)}
          layout={nativeMotion(GLYPH_LAYOUT)}
        >
          {/* The fade rides on the text, not on the box: one view driving both
              an animated style and a layout animation breaks reanimated. */}
          <AnimatedText
            weight="medium"
            numberOfLines={1}
            style={[
              {
                fontSize,
                lineHeight,
                letterSpacing: fontSize * LETTER_SPACING_RATIO,
                color: hasError
                  ? theme.content.elements.error
                  : theme.content.elements.primary,
                opacity: kind === 'placeholder' ? PLACEHOLDER_OPACITY : 1,
              },
              kind === 'prefix' ? prefixDimming : null,
            ]}
          >
            {char}
          </AnimatedText>
        </AnimatedBox>
      ))}
    </Box>
  )
}

/**
 * Which side of the row a character comes from and goes back to. The prefix
 * travels with whatever it stands next to, so the pair never crosses.
 */
function glyphSide(kind: AmountGlyphKind, isEmpty: boolean) {
  if (kind === 'placeholder') {
    return 'above'
  }
  if (kind === 'prefix' && isEmpty) {
    return 'above'
  }
  return 'below'
}

/** Enters from `offset` and leaves back to it — a negative one runs upwards. */
function slideKeyframes(offset: number) {
  return {
    entering: new Keyframe({
      0: { transform: [{ translateY: offset }] },
      100: { transform: [{ translateY: 0 }], easing: MOTION_EASING },
    }).duration(MOTION_DURATION),
    exiting: new Keyframe({
      0: { transform: [{ translateY: 0 }] },
      100: { transform: [{ translateY: offset }], easing: MOTION_EASING },
    }).duration(MOTION_DURATION),
  }
}

/** The largest size that keeps `text` on one line within `available`. */
function fitAmountFontSize(text: string, available: number) {
  const fitted = available / (text.length * CHARACTER_ADVANCE_RATIO)
  return Math.max(MIN_FONT_SIZE, Math.min(AMOUNT_FONT_SIZE, fitted))
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignSelf: 'stretch',
    overflow: 'hidden',
  },
})
